import { useState } from 'react';
import { useCustomerTickets } from '@/api/customer-tickets';
import { useCustomerServicesForSla, useCustomerUptimeStats } from '@/api/customer-sla';
import { TabError, TabLoading } from '../tab-states';
import { UptimeOverview } from './uptime-overview';
import { TicketsSummary } from './tickets-summary';
import { ServiceSlaGrid } from './service-sla-grid';

const PAGE_SIZE = 20;

/**
 * Customer SLA & Tickets tab — an SLA dashboard for one customer, in the legacy
 * ManageSLA order: (1) uptime overview band, (2) tickets summary with a
 * collapsible ticket table, (3) the per-service SLA cards grid. Read-only; SLA
 * links point at the service SLA route.
 */
export default function SlaTicketsTab({ customerId }: { customerId: number }) {
  const [page, setPage] = useState(1);

  const uptime = useCustomerUptimeStats(customerId);
  const services = useCustomerServicesForSla(customerId);
  const tickets = useCustomerTickets(customerId, { page, page_size: PAGE_SIZE });

  if (uptime.isError || services.isError || tickets.isError) {
    return (
      <TabError
        title="Failed to load SLA & tickets"
        onRetry={() => {
          uptime.refetch();
          services.refetch();
          tickets.refetch();
        }}
      />
    );
  }

  if (uptime.isLoading || services.isLoading || tickets.isLoading) {
    return <TabLoading rows={8} />;
  }

  const serviceRows = services.data?.results ?? [];
  const ticketRows = tickets.data?.results ?? [];
  const ticketTotal = tickets.data?.pagination?.total ?? ticketRows.length;
  const ticketPageCount = tickets.data?.pagination?.total_pages ?? 1;

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {uptime.data?.result ? <UptimeOverview stats={uptime.data.result} /> : null}
      <ServiceSlaGrid services={serviceRows} />
      <TicketsSummary
        rows={ticketRows}
        total={ticketTotal}
        page={page}
        pageSize={PAGE_SIZE}
        pageCount={ticketPageCount}
        onPageChange={setPage}
        services={serviceRows}
      />
    </div>
  );
}
